import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import OwnerLayout from "@/components/OwnerLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// Turns {length, min_letters} into the one-line rule shown next to the PIN
// fields, so nobody has to guess the format and get a 400 back.
function pinRuleText(policy) {
  const length = policy?.length ?? 7;
  const minLetters = policy?.min_letters ?? 0;
  if (minLetters === 0) return `${length} digits, numbers only.`;
  return `${length} characters, at least ${minLetters} letter${minLetters === 1 ? "" : "s"} mixed in.`;
}

function PinSection({ user, setUser }) {
  const { settings } = useSettings();
  const policy = settings?.pin_policy || { length: 7, min_letters: 0 };
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  // Only an admin can flip pin_enabled for an agent (from the Team page).
  // An admin can flip their own, right here, since there's no way to lock
  // yourself out by doing it — the password always still works.
  const canSelfToggle = user?.role === "admin";

  const toggleForSelf = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.user_id}/pin`, { pin_enabled: next });
      setUser({ ...user, pin_enabled: next, has_pin: next ? user.has_pin : false });
      toast.success(next ? "PIN sign-in turned on" : "PIN sign-in turned off");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setupPin = async () => {
    if (pin !== confirmPin) {
      toast.error("Those two PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/pin/setup", { pin });
      setUser({ ...user, has_pin: true });
      setPin("");
      setConfirmPin("");
      toast.success("PIN saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removePin = async () => {
    setBusy(true);
    try {
      await api.post("/auth/pin/disable");
      setUser({ ...user, has_pin: false });
      toast.success("PIN removed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel panel-pad">
      <div className="flex items-center justify-between">
        <div className="eyebrow">PIN sign-in</div>
        {canSelfToggle && (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-muted2">{user?.pin_enabled ? "On" : "Off"}</span>
            <Switch checked={!!user?.pin_enabled} disabled={busy} onCheckedChange={toggleForSelf} />
          </div>
        )}
      </div>
      <div className="mt-4">
        {!user?.pin_enabled ? (
          <p className="text-[13px] text-muted2 leading-relaxed">
            {canSelfToggle
              ? "Turn this on to sign in faster on devices you already trust, without typing your full password."
              : "Not turned on for your account yet. Ask your admin to enable PIN sign-in for you."}
          </p>
        ) : (
          <div className="space-y-4">
            {user?.has_pin && (
              <div className="flex items-center justify-between text-[13px] text-muted2">
                <span>A PIN is set on this account.</span>
                <Button size="sm" variant="ghost" onClick={removePin} disabled={busy} className="text-red-600 hover:text-red-700">
                  Remove PIN
                </Button>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="pin-new">{user?.has_pin ? "Change PIN" : "Set a PIN"}</Label>
              <PasswordInput
                id="pin-new"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pin-confirm">Confirm PIN</Label>
              <PasswordInput
                id="pin-confirm"
                autoComplete="off"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
              />
              <p className="text-[12px] text-muted2">{pinRuleText(policy)}</p>
            </div>
            <Button
              onClick={setupPin}
              disabled={busy || !pin || !confirmPin}
              className="bg-brand hover:bg-brand-hover"
            >
              Save PIN
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  // The owner has no client context, so their profile sits in the owner shell.
  const Shell = user?.role === "owner" ? OwnerLayout : Layout;
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const saveName = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { name });
      setUser(data);
      toast.success("Name updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Your profile" subtitle={user?.email}>
      <div className="grid lg:grid-cols-2 gap-5 max-w-4xl">
        <div className="panel panel-pad">
          <div className="eyebrow">Details</div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pname">Name</Label>
              <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled />
            </div>
            <div className="flex items-center justify-between text-[13px] text-muted2">
              <span>Role</span>
              <span className="capitalize text-ink font-medium">{user?.role}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] text-muted2">
              <span>Joined</span>
              <span className="text-ink">{formatDate(user?.created_at)}</span>
            </div>
            <Button onClick={saveName} disabled={busy} className="bg-brand hover:bg-brand-hover">
              Save changes
            </Button>
          </div>
        </div>

        <div className="panel panel-pad">
          <div className="eyebrow">Password</div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Current password</Label>
              <PasswordInput
                id="cur"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <PasswordInput
                id="new"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              />
              <p className="text-[12px] text-muted2">At least 8 characters.</p>
            </div>
            <Button
              onClick={savePassword}
              disabled={busy || !pw.current_password || !pw.new_password}
              className="bg-brand hover:bg-brand-hover"
            >
              Change password
            </Button>
          </div>
        </div>

        {user?.role !== "owner" && <PinSection user={user} setUser={setUser} />}
      </div>
    </Shell>
  );
}
